import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';

import { Folders } from '../../../@types/uploaded-files.type';
import { uploadFiles } from '../../../api/uploadedFiles';
import { NodeAttributeType, ServiceAttachmentUploadFeature } from '../../../configs/enums';
import { useGetPartActionTypes } from '../../../queries/partAction-query';
import { useGetServiceCheckInData } from '../../../queries/service-checkin-checkout-query';
import { useGetServiceLogsByServiceId } from '../../../queries/service-log-query';
import {
  useGetServiceById,
  useGetServicesNodesLocationRelatedAttributes,
} from '../../../queries/service-query';
import { useGetNextServicesStatuses } from '../../../queries/service-status-query';
import { useGetSLAsByServiceId } from '../../../queries/sla-query';
import { useGetParentAttributesByPropertyType } from '../../../queries/structure-query';
import { useGetUploadedFiles } from '../../../queries/uploadedfiles-query';

export const useMobileServiceDetail = (serviceId: number, isValidId: boolean) => {
  // Fetch service data
  const {
    data: service,
    isLoading: isLoadingService,
    isError: isServiceError,
    refetch: refetchService,
  } = useGetServiceById(isValidId ? serviceId : 0);

  // Fetch service logs
  const {
    data: serviceLogs,
    isLoading: isLoadingLogs,
    refetch: refetchLogs,
  } = useGetServiceLogsByServiceId(isValidId ? serviceId : 0);

  // Fetch check-in data
  const { refetch: refetchCheckIn } = useGetServiceCheckInData(isValidId ? serviceId : 0);

  // Fetch next available statuses
  const { data: nextStatuses, refetch: refetchStatuses } = useGetNextServicesStatuses(
    service?.serviceStatusCode || '',
    service?.serviceTypeCode || '',
    serviceId
  );

  // Fetch location attributes
  const {
    data: locationAttributes,
    isLoading: isLoadingLocation,
    refetch: refetchLocation,
  } = useGetServicesNodesLocationRelatedAttributes(
    serviceId,
    service?.mainLocationNodeId || 0,
    service?.subLocationNodeId || 0
  );

  // Fetch trading hours
  const { data: tradingHours, refetch: refetchTradingHours } = useGetParentAttributesByPropertyType(
    service?.mainLocationNodeId?.toString() || '0',
    NodeAttributeType.TradingHours,
    !service?.isSiblingInServiceClientHierarchy ? service?.id : undefined
  );

  // Fetch location reference numbers
  const { data: locationReferenceNumbers, refetch: refetchLocationRefs } =
    useGetParentAttributesByPropertyType(
      service?.mainLocationNodeId?.toString() || '0',
      NodeAttributeType.ReferenceNumbers,
      !service?.isSiblingInServiceClientHierarchy ? service?.id : undefined
    );

  // Fetch location contacts
  const { data: locationContacts, refetch: refetchLocationContacts } =
    useGetParentAttributesByPropertyType(
      service?.mainLocationNodeId?.toString() || '0',
      NodeAttributeType.Contact,
      !service?.isSiblingInServiceClientHierarchy ? service?.id : undefined
    );

  // Fetch coverage hours
  const { data: coverageHours, refetch: refetchCoverageHours } =
    useGetParentAttributesByPropertyType(
      service?.mainLocationNodeId?.toString() || '0',
      NodeAttributeType.Coverages,
      !service?.isSiblingInServiceClientHierarchy ? service?.id : undefined
    );

  // Fetch attachments
  const {
    data: attachments,
    isLoading: isLoadingAttachments,
    refetch: refetchAttachments,
  } = useGetUploadedFiles(serviceId, Folders.ServiceAttachments);

  // Fetch part action types
  const { data: partActionTypes, refetch: refetchPartActionTypes } = useGetPartActionTypes();

  // Fetch per-service SLAs (includes targetTypeCode for attendance prioritisation)
  const { data: serviceSLAsRaw, refetch: refetchSLAs } = useGetSLAsByServiceId(
    isValidId ? serviceId : 0
  );

  // The query returns any — cast to array of SLAContactRowData
  const serviceSLAs = Array.isArray(serviceSLAsRaw) ? serviceSLAsRaw : [];

  // Upload files mutation
  const uploadFilesMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('fileList', file);
      });
      return uploadFiles(
        serviceId,
        formData,
        Folders.ServiceAttachments,
        ServiceAttachmentUploadFeature.ReportedInfo
      );
    },
    onSuccess: () => {
      refetchAttachments();
    },
  });

  // Fetch status data when service is loaded
  useEffect(() => {
    if (service?.serviceStatusCode && service?.serviceTypeCode) {
      refetchStatuses();
    }
  }, [service?.serviceStatusCode, service?.serviceTypeCode, refetchStatuses]);

  // Fetch attachment data when serviceId is available
  useEffect(() => {
    if (serviceId) {
      refetchAttachments();
    }
  }, [serviceId, refetchAttachments]);

  // Fetch location data when service location IDs are available
  useEffect(() => {
    if (service?.mainLocationNodeId) {
      refetchLocation();
      refetchTradingHours();
      refetchLocationRefs();
      refetchLocationContacts();
      refetchCoverageHours();
    }
  }, [
    service?.mainLocationNodeId,
    service?.subLocationNodeId,
    refetchLocation,
    refetchTradingHours,
    refetchLocationRefs,
    refetchLocationContacts,
    refetchCoverageHours,
  ]);

  // Fetch part action types on mount
  useEffect(() => {
    refetchPartActionTypes();
  }, [refetchPartActionTypes]);

  // Targeted refresh functions — only refetch what actually changed
  const handleNoteRefresh = async (): Promise<void> => {
    await refetchLogs();
  };

  const handleStatusRefresh = async (): Promise<void> => {
    await Promise.all([
      refetchService(),
      refetchLogs(),
      refetchStatuses(),
      refetchCheckIn(),
      refetchSLAs(),
    ]);
  };

  const handlePartActionRefresh = async (): Promise<void> => {
    await Promise.all([refetchService(), refetchLogs()]);
  };

  const handleAttachmentRefresh = async (): Promise<void> => {
    await refetchAttachments();
  };

  return {
    service,
    serviceSLAs,
    serviceLogs,
    nextStatuses,
    locationAttributes,
    tradingHours,
    locationReferenceNumbers,
    locationContacts,
    coverageHours,
    attachments,
    partActionTypes,
    isLoadingService,
    isLoadingLogs,
    isLoadingLocation,
    isLoadingAttachments,
    isServiceError,
    handleStatusRefresh,
    handleNoteRefresh,
    handlePartActionRefresh,
    handleAttachmentRefresh,
    refetchAttachments,
    uploadFiles: uploadFilesMutation.mutateAsync,
    isUploadingFiles: uploadFilesMutation.isPending,
  };
};
